import { CSSProperties, FC, useEffect, useRef, useState } from "react";
import videojs from "video.js";
import Player from "video.js/dist/types/player";
import "video.js/dist/video-js.css";
import "@videojs/http-streaming";
import ChatMessage from "../chat/CommentArea";
import { PlayerStatus, VideoJsOptions } from "@/types";
import PlayReadyModal from "./PlayReadyModal";
import { FaPlay, FaPause, FaVolumeMute, FaVolumeUp } from "react-icons/fa";
import { useTranslation } from "react-i18next";

interface CustomControlsProps {
  player: Player | undefined;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  isPlaying: boolean;
  onVolumeChange: (value: number) => void;
  onMuteToggle: () => void;
  onSeek: (value: number) => void;
  onPlayPause: () => void;
  isLandscapeFullScreen?: boolean;
}

const CustomControls: FC<CustomControlsProps> = ({
  player,
  currentTime,
  duration,
  volume,
  isMuted,
  isPlaying,
  onVolumeChange,
  onMuteToggle,
  onSeek,
  onPlayPause,
  isLandscapeFullScreen = false,
}) => {
  const [isControlsVisible, setIsControlsVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const controlsTimeoutRef = useRef<NodeJS.Timeout>();
  const progressBarRef = useRef<HTMLDivElement>(null);

  // ライブ配信かどうかを判定
  const isLive = !isFinite(duration) || duration === 0;

  // シーク位置を計算する関数
  const calculateSeekPosition = (clientX: number) => {
    if (!progressBarRef.current) return null;
    const rect = progressBarRef.current.getBoundingClientRect();
    const pos = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return pos * duration;
  };

  // マウスドラッグ開始
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    const seekTime = calculateSeekPosition(e.clientX);
    if (seekTime !== null) {
      onSeek(seekTime);
    }
  };

  // タッチ開始
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    const touch = e.touches[0];
    const seekTime = calculateSeekPosition(touch.clientX);
    if (seekTime !== null) {
      onSeek(seekTime);
    }
  };

  // タッチ移動
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const seekTime = calculateSeekPosition(touch.clientX);
    if (seekTime !== null) {
      onSeek(seekTime);
    }
  };

  // タッチ終了
  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // マウスドラッグ中・終了のイベントリスナー
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const seekTime = calculateSeekPosition(e.clientX);
      if (seekTime !== null) {
        onSeek(seekTime);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, duration, onSeek]);

  const showControls = () => {
    setIsControlsVisible(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setIsControlsVisible(false);
    }, 3000); // 3秒後に非表示
  };

  const formatTime = (seconds: number) => {
    // ライブ配信の場合は時間を表示しない
    if (!isFinite(seconds)) {
      return "LIVE";
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const wrapperClass = isLandscapeFullScreen
    ? "tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-10 tw-pointer-events-none"
    : "tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-w-full tw-h-full tw-z-10 tw-pointer-events-none";

  const barClass = `tw-bg-black/50 tw-p-2 tw-flex tw-items-center tw-gap-2 tw-transition-opacity tw-duration-300 tw-pointer-events-auto ${
    isLandscapeFullScreen
      ? "tw-relative tw-mx-auto tw-max-w-[min(100%,960px)] tw-rounded-t-2xl tw-border-t tw-border-white/10 tw-pb-[calc(0.5rem+env(safe-area-inset-bottom))]"
      : "tw-absolute tw-bottom-0 tw-left-0 tw-right-0"
  } ${isControlsVisible ? "tw-opacity-100" : "tw-opacity-0"}`;

  return (
    <div
      className={wrapperClass}
      onMouseMove={showControls}
      onTouchStart={showControls}
    >
      <div className={barClass}>
        {/* Play/Pause Button */}
        <button className="tw-text-white tw-p-1" onClick={onPlayPause}>
          {isPlaying ? <FaPause size={16} /> : <FaPlay size={16} />}
        </button>

        {/* Time Display */}
        <div className="tw-text-white tw-text-xs tw-min-w-[70px]">
          {isLive ? (
            <span className="tw-text-red-500 tw-font-bold">● LIVE</span>
          ) : (
            `${formatTime(currentTime)} / ${formatTime(duration)}`
          )}
        </div>

        {/* Progress Bar - ライブ配信では非表示 */}
        {!isLive && (
          <div
            ref={progressBarRef}
            className="tw-flex-1 tw-h-1 tw-bg-gray-600 tw-rounded-full tw-cursor-pointer"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="tw-h-full tw-bg-white tw-rounded-full tw-pointer-events-none"
              style={{ width: `${(currentTime / duration) * 100}%` }}
            />
          </div>
        )}

        {/* Volume Controls */}
        <button className="tw-text-white tw-p-1" onClick={onMuteToggle}>
          {isMuted || volume === 0 ? (
            <FaVolumeMute size={16} />
          ) : (
            <FaVolumeUp size={16} />
          )}
        </button>
      </div>
    </div>
  );
};

export interface VideoJSPlayerProps extends VideoJsOptions {
  status: PlayerStatus;
  startAt: string;
  thumbnail: string;
  privacyPolicyUrl: string;
  id: string;
  subtitleUrls?: { [lang: string]: string };
  fullScreen?: boolean;
  landscape?: boolean;
  dismissPlayReadyModal?: boolean;
}

export const VideoJsPlayer: FC<VideoJSPlayerProps> = ({
  id,
  sources,
  options,
  status,
  startAt,
  thumbnail,
  onReady,
  onStart,
  privacyPolicyUrl,
  subtitleUrls,
  fullScreen = false,
  landscape = false,
  dismissPlayReadyModal = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [player, setPlayer] = useState<Player | undefined>(undefined);
  const [isOpenModal, setIsOpenModal] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const { i18n } = useTranslation();
  const isLandscapeFullScreen = Boolean(landscape && fullScreen);
  const playerWrapperStyle: CSSProperties = {
    width: "100%",
    height: "100%",
    aspectRatio: landscape ? "16 / 9" : "9 / 16",
  };

  useEffect(() => {
    if (dismissPlayReadyModal) {
      setIsOpenModal(false);
    }
  }, [dismissPlayReadyModal]);

  useEffect(() => {
    setIsOpenModal(true);
  }, [id]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || sources.length === 0) return;

    const videojsOptions = {
      sources,
      ...options,
      poster: thumbnail,
      controls: false,
      html5: {
        nativeTextTracks: false,
      },
    };

    const player = videojs(video, videojsOptions);

    player.on("timeupdate", () => {
      const time = player.currentTime();
      if (typeof time === "number") {
        setCurrentTime(time);
      }
    });

    player.on("loadedmetadata", () => {
      const dur = player.duration();
      if (typeof dur === "number") {
        setDuration(dur);
      }
    });

    player.on("volumechange", () => {
      const vol = player.volume();
      const muted = player.muted();
      if (typeof vol === "number") {
        setVolume(vol);
      }
      if (typeof muted === "boolean") {
        setIsMuted(muted);
      }
    });

    player.on("play", () => {
      setIsPlaying(true);
      setHasStarted(true);
    });

    player.on("pause", () => {
      setIsPlaying(false);
    });

    player.on("ended", () => {
      setIsPlaying(false);
      setHasStarted(false);
    });

    player.on("ready", () => {
      onReady?.(player);
    });
    setPlayer(player);
    if (status === "onAir") {
      setHasStarted(true);
    }
  }, [sources, options, thumbnail]);

  // 字幕トラックの追加
  useEffect(() => {
    if (!player || !subtitleUrls) return;

    const setupSubtitles = () => {
      // 既存の字幕トラックを削除（重複防止）
      const textTracks = (player as any).textTracks?.();
      if (textTracks) {
        for (let i = textTracks.length - 1; i >= 0; i--) {
          const track = textTracks[i];
          if (track.kind === "captions") {
            player.removeRemoteTextTrack(track);
          }
        }
      }

      // 字幕トラックを追加
      Object.entries(subtitleUrls).forEach(([lang, url]) => {
        if (url) {
          player.addRemoteTextTrack(
            {
              kind: "captions",
              src: url,
              srclang: lang,
              label:
                lang === "ja"
                  ? "日本語"
                  : lang === "en"
                    ? "English"
                    : lang === "zh"
                      ? "中文"
                      : lang,
              default: false,
            },
            false
          );
        }
      });
    };

    // tech が初期化されているか確認してから字幕を追加
    if ((player as any).tech_ && player.readyState() >= 1) {
      setupSubtitles();
    } else {
      player.ready(setupSubtitles);
    }
  }, [player, subtitleUrls]);

  // 言語変更時に字幕を切り替え
  useEffect(() => {
    if (!player || !subtitleUrls) return;

    const switchSubtitle = () => {
      const currentLang = i18n.language;
      const textTracks = (player as any).textTracks?.();

      if (!textTracks) return;

      for (let i = 0; i < textTracks.length; i++) {
        const track = textTracks[i];
        // 現在の言語と一致する字幕トラックのみ表示
        if (track.language === currentLang && subtitleUrls[currentLang]) {
          track.mode = "showing";
        } else {
          track.mode = "disabled";
        }
      }
    };
    switchSubtitle();
  }, [player, subtitleUrls, i18n.language]);

  const handleVolumeChange = (value: number) => {
    if (player) {
      player.volume(value);
      if (value === 0) {
        player.muted(true);
      } else if (player.muted()) {
        player.muted(false);
      }
    }
  };

  const handleMuteToggle = () => {
    if (player) {
      player.muted(!player.muted());
    }
  };

  const handleSeek = (value: number) => {
    if (player) {
      player.currentTime(value);
    }
  };

  const handlePlayPause = () => {
    if (player) {
      if (player.paused()) {
        player.play();
        setIsOpenModal(false);
      } else {
        player.pause();
      }
    }
  };

  return (
    <>
      <style>
        {`
        .video-js {
          width: 100%;
          height: 100%;
        }
        .video-js .vjs-tech {
          object-fit: ${landscape ? "contain" : "cover"};
          width: 100%;
          height: 100%;
        }
        .video-js .vjs-control-bar {
          display: none;
        }
        .video-js .vjs-text-track-display {
          position: absolute;
          bottom: 4em !important;
          left: 0;
          right: 0;
          top: 0;
          pointer-events: none;
          z-index: 1;
        }
        .video-js .vjs-text-track-display > div {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          pointer-events: none;
        }
        .video-js .vjs-text-track-display > div > div > div,
        .video-js video::cue {
          background-color: rgba(0, 0, 0, 0.6) !important;
          padding: 8px;
          font-size: 0.4em;
          line-height: normal !important;
          display: inline-block !important;
          border-radius: 8px !important;
        }
        .video-js .vjs-poster {
         background-color: rgba(0, 0, 0, 0.6) !important;
        }
        `}
      </style>
      <div data-vjs-player className="tw-h-full tw-relative">
        <video ref={videoRef} className="video-js" data-foove-player-id={id} muted={isMuted} playsInline />
        {hasStarted && (
          <CustomControls
            player={player}
            currentTime={currentTime}
            duration={duration}
            volume={volume}
            isMuted={isMuted}
            isPlaying={isPlaying}
            onVolumeChange={handleVolumeChange}
            onMuteToggle={handleMuteToggle}
            onSeek={handleSeek}
            onPlayPause={handlePlayPause}
            isLandscapeFullScreen={isLandscapeFullScreen}
          />
        )}
      </div>
      <div id={`play-ready-modal-${id}`} className="tw-absolute tw-inset-0"></div>
      <PlayReadyModal
        id={id}
        player={player}
        open={isOpenModal}
        onClose={() => {
          setIsOpenModal(false);
        }}
        status={status}
        startAt={startAt}
        thumbnail={thumbnail}
        privacyPolicyUrl={privacyPolicyUrl}
        landscape={landscape}
        onStart={() => {
          onStart?.(player);
        }}
      ></PlayReadyModal>
    </>
  );
};

export default VideoJsPlayer;
